// @flow
import React, { Component, Fragment } from 'react'
import _ from 'lodash'
import { BodyRow, Cell } from '../MainTable'
import { Audience } from '../constants'
import Spinner from '../controls/Spinner'
import { getData } from '../../components/AudienceEdit/Estimates/loader'
import EstimatesLoadingWatcher from '../../services/callbacksDebouncer'
import styled from 'styled-components'
import hash from 'object-hash'
import postJson from '../../services/postJson'

const SegmentBodyRow: typeof BodyRow = styled(BodyRow)`
  background-color: ${(props: Object) => (props.error ? '#f5c6cb' : '')};
`

const GET_ESTIMATES_PATH = '/api/v1/estimates'

type Props = {|
  autoBids: Object,
  paymentType: string,
  segmentByProviders: boolean,
  providers: Array<string>,
  segmentItems: Array<Object>
|}

type State = {|
  requestsResults: Object,
  isError: Object
|}

export default class SegmentationItems extends Component<Props, State> {
  segmentationEstimatesWatcher: any
  segmentParamsQueue: Array<Object> = []
  isProcessing: boolean = false // protection from parallel processing

  constructor(props: Props) {
    super(props)
    this.state = {
      requestsResults: {},
      isError: {}
    }

    this.segmentationEstimatesWatcher = new EstimatesLoadingWatcher(1500)
    this.segmentationEstimatesWatcher.onChange(() => this.getSegmentsEstimates())
  }

  componentDidUpdate(prevProps: Props) {
    if (!_.isEqual(prevProps.segmentItems, this.props.segmentItems)) {
      this.segmentationEstimatesWatcher.debouncedOnChange()
    }
  }

  render() {
    return (
      <tbody>
        {_.map(this.props.segmentItems, (item, index: number) => {
          const itemHash = hash(getData(item))
          const isLoading =
            this.isProcessing &&
            !this.state.requestsResults[itemHash] &&
            !this.state.isError[itemHash]
          return (
            <Fragment key={index}>
              <SegmentBodyRow error={this.state.isError[itemHash]}>
                <Cell className="t-SegmentItemName">{item.name || ''}</Cell>
                <Cell className="t-SegmentItemUsers">
                  {isLoading ? <Spinner /> : this.segmentItemUsers(itemHash)}
                </Cell>
                <Cell className="t-SegmentItemPrice">
                  {isLoading ? <Spinner /> : this.segmentItemPrice(itemHash)}
                </Cell>
              </SegmentBodyRow>
            </Fragment>
          )
        })}
      </tbody>
    )
  }

  // get estimates for every segmented audience
  // if the segmentation is not by providers but user checked more than one provider
  // then do not send request
  getSegmentsEstimates() {
    const { segmentItems, providers, segmentByProviders } = this.props
    if (!segmentByProviders && _.size(providers) > 1) return

    const segmentsParams = _.map(segmentItems, (segment) => {
      return getData(segment)
    })
    this.segmentParamsQueue = [...this.segmentParamsQueue, ...segmentsParams]
    this.processQueue()
  }

  async processQueue() {
    if (this.isProcessing || this.segmentParamsQueue.length === 0) return

    const [currentParams, ...newSegmentParamsQueue] = this.segmentParamsQueue
    this.isProcessing = true
    this.segmentParamsQueue = newSegmentParamsQueue

    await this.getSingleSegmentEstimates(currentParams)

    this.isProcessing = false
    this.processQueue()
  }

  async getSingleSegmentEstimates(params: Object): Promise<Object> {
    const paramsHash = hash(params)

    // is the current item visible?
    if (_.every(this.props.segmentItems, (item) => paramsHash !== hash(getData(item)))) return

    if (_.includes(params.providers, 'google')) {
      this.setState({ requestsResults: { ...this.state.requestsResults, [paramsHash]: [] } })
      return
    }
    try {
      if (_.isEmpty(this.state.requestsResults[paramsHash])) {
        const response = await this.requestEstimates(params)
        const priceRecommentations = _.first(response).estimates
        this.setState({
          requestsResults: { ...this.state.requestsResults, [paramsHash]: priceRecommentations }
        })
      }
    } catch (e) {
      this.setState({ isError: { ...this.state.isError, [paramsHash]: true } })
    }
  }

  requestEstimates(params: Object): Promise<Object> {
    return new Promise((resolve, reject) => {
      postJson(GET_ESTIMATES_PATH, params)
        .then((response) => {
          resolve(response)
        })
        .fail(() => {
          reject(new window.Error('request failed'))
        })
    })
  }

  segmentItemUsers(itemHash: string) {
    const { requestsResults } = this.state
    const segmentEstimates = requestsResults[itemHash]
    if (_.isEmpty(segmentEstimates) || this.providerErrorCheck(segmentEstimates)) return 'n/a'
    return _.first(segmentEstimates)['users'].toLocaleString('ru-RU')
  }

  segmentItemPrice(itemHash: string) {
    const { requestsResults } = this.state
    const segmentEstimates = requestsResults[itemHash]
    if (_.isEmpty(segmentEstimates) || this.providerErrorCheck(segmentEstimates)) return 'n/a'
    if (this.props.paymentType === Audience.payment_types.cpc) {
      return _.ceil(_.first(segmentEstimates)['cpc_effective'], 2)
    } else if (this.props.paymentType === Audience.payment_types.cpm) {
      return _.ceil(_.first(segmentEstimates)['cpm_effective'], 2)
    } else if (this.props.paymentType === Audience.payment_types.cpa) {
      return _.ceil(_.first(segmentEstimates)['cpa_effective'], 2)
    } else {
      return this.averageAutoPrice(segmentEstimates)
    }
  }

  providerErrorCheck(segmentEstimates: Array<Object>) {
    return _.every(segmentEstimates, (estimate) => estimate.provider_error)
  }

  averageAutoPrice(segmentEstimates: Array<Object>) {
    const providers = _(segmentEstimates)
      .filter((estimate) => !estimate.provider_error)
      .map((estimate) => estimate.provider)
      .value()
    if (_.isEmpty(providers)) return 'n/a'
    const sum = _.sumBy(providers, (provider) => this.props.autoBids[provider])
    return _.ceil(sum / _.size(providers), 2)
  }
}
